import { Action, PathsV1CompanyUuidGetParametersQueryExtend } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import {
  useCompanyByUuidQuery,
  type CompanyByUuidParameters,
  type CompanyByUuidResponse,
} from "@/common/api/company/use-company-by-uuid.query";
import { CompanyLogo, CompanyLogoVariant } from "@/components/company-logo/company-logo";
import { DetailHeader } from "@/components/detail-page/detail-header";
import { DetailPageLayout } from "@/components/detail-page/detail-page-layout";
import { DetailPageSkeleton } from "@/components/detail-page/detail-page-skeleton";
import { DetailTabs } from "@/components/detail-page/detail-tabs";
import type { DetailAction, DetailTab } from "@/components/detail-page/types";
import { CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { Building2, Fingerprint, PencilIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { OverviewTab } from "./tabs/overview-tab/overview-tab";
import { SystemIdsTab } from "./tabs/system-ids-tab/system-ids-tab";

const COMPANY_DETAIL_QUERY_PARAMS = {
  parameters: {
    extend: [
      PathsV1CompanyUuidGetParametersQueryExtend.CompanyIdentifiers,
      PathsV1CompanyUuidGetParametersQueryExtend.CompanyGroup,
    ],
  },
};

const COMPANY_DETAIL_TRACKING_NAME = "company";

export function CompanyDetailView(): ReactElement {
  return (
    <Can I="read" a="Company">
      <CompanyDetailQuery />
    </Can>
  );
}

function CompanyDetailQuery(): ReactElement {
  const { t } = useTranslation(["masterdata"]);
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid ?? "";
  const companyQuery = useCompanyByUuidQuery(uuid, COMPANY_DETAIL_QUERY_PARAMS);

  if (companyQuery.isPending) {
    return <DetailPageSkeleton />;
  }

  return (
    <UuidExistenceWrapper<CompanyByUuidParameters, CompanyByUuidResponse>
      uuid={uuid}
      query={useCompanyByUuidQuery}
      queryResult={companyQuery}
      notFoundProps={{
        title: t("masterdata:CompanyNotFound"),
        message: t("masterdata:CompanyDoesNotExist"),
        description: t("masterdata:CheckCompanyIdOrContactSupport"),
        backTo: "~/masterdata/company",
        backToLabel: t("masterdata:BackToCompanyOverview"),
      }}
    >
      {companyQuery.data ? <CompanyDetailContent company={companyQuery.data} /> : null}
    </UuidExistenceWrapper>
  );
}

function CompanyDetailContent({ company }: { company: CompanyByUuidResponse }): ReactElement {
  const { t } = useTranslation(["masterdata", "common"]);
  const [, navigate] = useLocation();
  const isDeleted = Boolean(company.deletedAt);

  const actions: DetailAction[] = isDeleted
    ? []
    : [
        {
          id: "edit",
          label: t("common:Edit"),
          icon: PencilIcon,
          permission: { action: Action.Manage, subject: "Company" },
          onClick: () => navigate(`~/masterdata/company/${company.uuid}/update`),
        },
      ];

  const tabs: DetailTab[] = [
    {
      id: "overview",
      label: t("common:Overview"),
      icon: Building2,
      content: () => <OverviewTab company={company} />,
    },
    {
      id: "identifiers",
      label: t("masterdata:SystemIds"),
      icon: Fingerprint,
      content: () => <SystemIdsTab company={company} />,
    },
  ];

  return (
    <DetailPageLayout
      variant={CenteredLayoutVariant.Wide}
      header={
        <DetailHeader
          icon={company.logoUrl ? <CompanyLogo company={company} variant={CompanyLogoVariant.MEDIUM} /> : <Building2 />}
          title={company.name}
          subtitle={company.companyGroup?.name}
          badges={
            isDeleted ? (
              <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Error}>
                {t("common:Deleted")}
              </Badge>
            ) : undefined
          }
          actions={actions}
        />
      }
    >
      <DetailTabs sections={tabs} trackingName={COMPANY_DETAIL_TRACKING_NAME} />
    </DetailPageLayout>
  );
}
